import * as React from "react";
import { useMutation } from "convex/react";
import { api } from "$convex/convexApi";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ComboboxSelect } from "@/components/ui/combobox-select";
import { useProductsWithDetails } from "@/hooks/useProducts";
import { useWarehouses } from "@/hooks/useReferenceData";
import { useAppLocale } from "@/lib/i18n";
import { dateInputStringToLocalStartOfDayMs } from "@/lib/format";
import { sortByLabel } from "@/lib/sort";
import { m } from "@/paraglide/messages.js";

interface CargoCreateDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const initialForm = {
  productId: "",
  warehouseId: "",
  status: "at_origin",
  totalBags: "",
  availableBags: "",
  pricePerPound: "",
  diff: "",
  arrivalDate: "",
  ociNumber: "",
  contractId: "",
};

function sanitizeContractPurchaseNumber(value: string): string {
  return value.toUpperCase().replace(/[^A-Z0-9-]/g, "").slice(0, 32);
}

function normalizeContractPurchaseNumber(value: string): string {
  return sanitizeContractPurchaseNumber(value.trim());
}

function toOptionalNumber(value: string) {
  if (value.trim() === "") return undefined;

  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : undefined;
}

function getStatusOptions() {
  return [
    { value: "at_origin", label: m.status_at_origin() },
    { value: "upcoming", label: m.status_upcoming() },
    { value: "in_transit", label: m.status_in_transit() },
    { value: "in_spot", label: m.status_in_spot() },
    { value: "sold_out", label: m.status_sold_out() },
    { value: "delivered", label: m.status_delivered() },
  ];
}

type CargoCreateProductOption = {
  _id: string;
  name?: string | null;
  weight?: number | null;
  origin?: {
    name?: string | null;
  } | null;
};

export function getCargoCreateProductOptionLabel(product: CargoCreateProductOption): string {
  const productName = String(product?.name ?? "").trim();
  const originName = String(product?.origin?.name ?? "").trim();

  if (!originName) return productName;
  return `${productName} (${originName})`;
}

export function sortCargoCreateProducts(
  products: readonly CargoCreateProductOption[] | undefined,
  locale: string,
): CargoCreateProductOption[] {
  return sortByLabel(products, getCargoCreateProductOptionLabel, locale);
}

export function CargoCreateDialog({ open, onOpenChange }: CargoCreateDialogProps) {
  const { locale } = useAppLocale();
  const [form, setForm] = React.useState(initialForm);
  const [saving, setSaving] = React.useState(false);
  const createCargo = useMutation(api.cargos.createCargo);
  const products = useProductsWithDetails();
  const warehouses = useWarehouses();
  const statusOptions = getStatusOptions();
  const sortedProducts = React.useMemo(
    () => sortCargoCreateProducts(products as CargoCreateProductOption[] | undefined, locale),
    [locale, products],
  );
  const productOptions = React.useMemo(
    () => sortedProducts.map((product) => ({
      value: product._id,
      label: getCargoCreateProductOptionLabel(product),
    })),
    [sortedProducts],
  );
  const selectedProduct = React.useMemo(
    () => sortedProducts.find((product) => product._id === form.productId),
    [form.productId, sortedProducts],
  );

  const set = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const handleCreate = async () => {
    if (!form.productId) {
      toast.error(m.cargo_product_required());
      return;
    }
    if (!form.totalBags || Number(form.totalBags) <= 0) {
      toast.error(m.cargo_total_bags_required());
      return;
    }
    if (
      !selectedProduct ||
      typeof selectedProduct.weight !== "number" ||
      !Number.isFinite(selectedProduct.weight) ||
      selectedProduct.weight <= 0
    ) {
      toast.error("Selected product must have a valid weight before creating cargo.");
      return;
    }
    setSaving(true);
    try {
      await createCargo({
        productId: form.productId as any,
        warehouseId: form.warehouseId || undefined,
        status: form.status || undefined,
        totalBags: Number(form.totalBags),
        availableBags: toOptionalNumber(form.availableBags),
        pricePerPound: toOptionalNumber(form.pricePerPound),
        diff: toOptionalNumber(form.diff),
        arrivalDate: dateInputStringToLocalStartOfDayMs(form.arrivalDate),
        ociNumber: form.ociNumber || undefined,
        contractId: normalizeContractPurchaseNumber(form.contractId) || undefined,
      });
      toast.success(m.cargo_created());
      setForm(initialForm);
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : m.cargo_create_failed());
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>{m.cargo_create()}</DialogTitle>
          <DialogDescription>{m.cargo_create_description()}</DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label>{m.cargo_product_label()}</Label>
            <ComboboxSelect
              options={productOptions}
              value={form.productId}
              onValueChange={(value) => set("productId", value)}
              placeholder={m.cargo_select_product()}
              searchPlaceholder="Search products..."
              emptyText="No products found."
              loading={products === undefined}
            />
          </div>

          <div className="grid gap-2">
            <Label>{m.warehouse()}</Label>
            <Select value={form.warehouseId} onValueChange={(v) => set("warehouseId", v)}>
              <SelectTrigger><SelectValue placeholder={m.cargo_select_warehouse()} /></SelectTrigger>
              <SelectContent>
                {warehouses?.map((w: any) => (
                  <SelectItem key={w._id} value={w._id}>{w.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label>{m.status()}</Label>
            <Select value={form.status} onValueChange={(v) => set("status", v)}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {statusOptions.map((s) => (
                  <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{m.cargo_total_bags_label()}</Label>
              <Input type="number" value={form.totalBags} onChange={(e) => set("totalBags", e.target.value)} placeholder="0" />
            </div>
            <div className="grid gap-2">
              <Label>{m.cargo_available_bags()}</Label>
              <Input type="number" value={form.availableBags} onChange={(e) => set("availableBags", e.target.value)} placeholder="0" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{m.cargo_price_per_lb_label()}</Label>
              <Input type="number" step="0.01" value={form.pricePerPound} onChange={(e) => set("pricePerPound", e.target.value)} placeholder="0.00" />
            </div>
            <div className="grid gap-2">
              <Label>{m.cargo_diff_label()}</Label>
              <Input type="number" step="0.01" value={form.diff} onChange={(e) => set("diff", e.target.value)} placeholder="0.00" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="grid gap-2">
              <Label>{m.cargo_arrival_date()}</Label>
              <DatePicker value={form.arrivalDate} onChange={(v) => set("arrivalDate", v)} placeholder={m.select_date()} />
            </div>
            <div className="grid gap-2">
              <Label>{m.cargo_oci_number()}</Label>
              <Input value={form.ociNumber} onChange={(e) => set("ociNumber", e.target.value)} placeholder="OCI-XXX" />
            </div>
          </div>

          <div className="grid gap-2">
            <Label>{m.cargo_contract_purchase_number()}</Label>
            <Input
              type="text"
              maxLength={32}
              value={form.contractId}
              onChange={(e) => set("contractId", sanitizeContractPurchaseNumber(e.target.value))}
              placeholder={m.cargo_contract_purchase_number_placeholder()}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{m.cancel()}</Button>
          <Button onClick={() => void handleCreate()} disabled={saving}>
            {saving ? m.creating() : m.cargo_create()}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
